import { useEffect, useState } from 'react'
import { Loader2, MessageCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { fetchMessagingSettings, updateMessagingSettings } from '@/lib/api/messagingSettings'
import { apiErrorMessage } from '@/lib/apiErrorMessage'

import { SettingsSection } from './SettingsSection'

/**
 * Booking confirmation toggle only — promos live on Send offers page.
 */
export function BookingMessagesSection({ embedded = false }) {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [confirmationEnabled, setConfirmationEnabled] = useState(true)
  const [statusLabel, setStatusLabel] = useState('')

  useEffect(() => {
    let active = true
    setLoading(true)

    fetchMessagingSettings()
      .then((payload) => {
        if (!active) return
        setConfirmationEnabled(payload.appointment_confirmation_enabled)
        setStatusLabel(payload.whatsapp?.status_label ?? '')
      })
      .catch((err) => {
        if (active) {
          setError(apiErrorMessage(err, 'Could not load message settings.'))
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  async function handleSave(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      await updateMessagingSettings({
        appointment_confirmation_enabled: confirmationEnabled,
      })
      setSuccess('Saved.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SettingsSection
      id="booking-messages"
      icon={MessageCircle}
      title="Booking messages"
      description="WhatsApp when someone books"
      embedded={embedded}
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          {statusLabel ? (
            <p className="text-sm text-muted-foreground">{statusLabel}</p>
          ) : null}

          <label className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/20 px-4 py-4">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5 rounded border-border"
              checked={confirmationEnabled}
              onChange={(event) => setConfirmationEnabled(event.target.checked)}
            />
            <span>
              <span className="block text-base font-medium text-foreground">
                Send WhatsApp when an appointment is booked
              </span>
              <span className="mt-1 block text-sm text-muted-foreground">
                The booking still saves even if the message does not go through.
              </span>
            </span>
          </label>

          <Button type="submit" disabled={submitting} className="rounded-xl">
            {submitting ? 'Saving…' : 'Save'}
          </Button>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="text-sm text-primary" role="status">
              {success}
            </p>
          ) : null}
        </form>
      )}
    </SettingsSection>
  )
}
